import React from 'react';

import type { ChainKey } from '@infinex/chain-config';
import { getActiveChains } from '@infinex/chain-config';
import type { BlockaidValidationResult } from '@infinex/connect-service-contract/client';
import { Text, ChainEmblem } from '@infinex/ui/components';

import { EvmContract } from './EvmContract';
import { EvmFunctionName } from './EvmFunctionName';
import { SolanaActions } from './SolanaActions';
import { SolanaPrograms } from './SolanaPrograms';

import { extractTransactionDetails } from '../helpers';

interface DetailsSectionProps {
  txnValidationResult: BlockaidValidationResult | null;
  chainKey: ChainKey;
}

export function DetailsSection({
  txnValidationResult,
  chainKey,
}: DetailsSectionProps) {
  if (!txnValidationResult) return null;

  const chainInfo = getActiveChains()[chainKey];

  // Get network display name
  const network = chainInfo?.displayName || chainKey;

  // Extract transaction details using helpers
  const details = extractTransactionDetails(txnValidationResult);
  console.log('Details section', details);
  if (!details) return null;

  console.log('Details', details);

  // Don't show section if we don't have meaningful data
  if (
    details.type === 'evm' &&
    !details.contractAddress &&
    !details.functionName
  )
    return null;
  if (
    details.type === 'solana' &&
    details.programs.length === 0 &&
    !details.actions
  )
    return null;

  // Handle explorer link click
  const handleExplorerClick = (address: string) => {
    if (!address || !chainInfo?.connectivity?.blockExplorers?.primary?.base)
      return;

    const explorerBase = chainInfo.connectivity.blockExplorers.primary.base;

    // Handle different explorer URL patterns
    let explorerUrl: string;
    if (chainKey === 'solana') {
      // Solana uses a different pattern
      explorerUrl = `${explorerBase}/account/${address}`;
    } else {
      // EVM chains use /address/
      explorerUrl = `${explorerBase}/address/${address}`;
    }

    window.open(explorerUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-surface mt-3 space-y-4 rounded-3xl px-5 py-6">
      <Text className="text-tertiary text-sm font-semibold">Details</Text>

      <div className="space-y-3">
        {/* Network */}
        {network && (
          <div className="flex items-center justify-between">
            <Text className="text-default text-sm font-medium">Network</Text>
            <div className="flex items-center gap-2">
              <ChainEmblem of={chainKey} size="xsmall" />
              <Text className="text-emphasis text-sm font-medium">
                {network}
              </Text>
            </div>
          </div>
        )}

        {/* Solana: Actions */}
        {details.type === 'solana' && details.actions && (
          <SolanaActions actions={details.actions} />
        )}

        {/* Solana: Programs and Accounts */}
        {details.type === 'solana' && details.programs.length > 0 && (
          <SolanaPrograms
            programs={details.programs}
            onExplorerClick={handleExplorerClick}
          />
        )}

        {/* EVM: Contract */}
        {details.type === 'evm' &&
          (details.contractAddress || details.contractName) && (
            <EvmContract
              contractAddress={details.contractAddress}
              contractName={details.contractName}
              onExplorerClick={handleExplorerClick}
            />
          )}

        {/* EVM: Function */}
        {details.type === 'evm' && details.functionName && (
          <EvmFunctionName functionName={details.functionName} />
        )}
      </div>
    </div>
  );
}
